import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  UserPlus, 
  Plus, 
  X, 
  Type,
  Target,
  Zap,
  User,
  ExternalLink,
  MapPin
} from 'lucide-react';

const ADS_FEATURE_ENABLED = false;
const AdsPage: React.FC = () => {
 const navigate = useNavigate();

 const handleGetStarted = () => {
  navigate('/practice/advertise/details');
 }

  const [activeTab, setActiveTab] = useState<'overview' | 'campaign'>('overview');
  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-megaphone w-10 h-10 text-purple-600">
                <path d="m3 11 18-5v12L3 14v-3z"></path>
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Start Advertising Your Practice</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Reach more clients and grow your practice with our targeted advertising platform. Connect with people who need your expertise.</p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Simple Pricing</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>No setup fees or contracts</p>
                <p>Set a daily budget, we bill you based on amount spent.</p>
                <p>We won't charge you more than your daily budget spend for 30.4 days</p>
                <p>10% fee on ad dollars spent</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Recommended Budgets</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>We Recommend Starting with</strong> $20/day per advertised clinician</p>
                <p>Start small and scale up as you get desired results</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-purple-600">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Partner Networks</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>We Advertise you in our partner networks to get your clinicians' scaleable targeted attention</p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Why Advertise With Us?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Qualified Leads</h4>
                  <p className="text-sm text-gray-600">Connect with clients actively seeking mental health services, after they got to know your clinicians</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-white">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Complete Control</h4>
                  <p className="text-sm text-gray-600">Set your budget, choose your clinicians, pause anytime</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Simplified System & Analytics</h4>
                  <p className="text-sm text-gray-600">We simplify advertising across the web and give you analytics for success</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Local Targeting</h4>
                  <p className="text-sm text-gray-600">Reach clients where you want.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <button 
              // className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700 transition-colors"
              className={`px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${ADS_FEATURE_ENABLED ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer' : 'bg-gray-400 text-gray-600 cursor-not-allowed'}`}
              // onClick={() => navigate('/practice/advertise/details')}
              // Handle click only if feature is enabled
              onClick={() => {
                if (ADS_FEATURE_ENABLED) {
                  navigate('/practice/advertise/details');
                }
              }}// else, do nothing
              disabled={!ADS_FEATURE_ENABLED}
              aria-disabled={!ADS_FEATURE_ENABLED}
            >
              Get Started with Advertising
            </button>
            <p className="text-sm text-gray-500 mt-3">Start reaching new clients today • No commitment required</p>
          </div>

        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default AdsPage;